import { ComponentType, useEffect } from 'react';
import {
	AiPowered,
	BookIcon,
	CheckingIcon,
	LogoText2,
	RepliesIcon
} from '@/resources/assets/images';
import { sendAnalytics } from '@/services/analyticsService';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '@/rootReducer';
import { useOnboardingContext } from '../context/OnboardingContext';
import { useUpdateOnboardingStatus } from '../hooks/useUpdateOnboardingStatus';

interface WelcomeCardProps {
	icon: ComponentType<{ className?: string }>;
	text: string;
	onClick: () => void;
}

const WelcomeCard = ({ icon: Icon, text, onClick }: WelcomeCardProps) => {
	return (
		<li className="aspect-[154/112] rounded-lg border-[0.0625rem] border-dm-black4 bg-dm-black3.5 shadow-welcomeCard duration-200 focus-within:scale-[1.005] focus-within:border-orange-brighter hover:scale-[1.005] hover:border-orange-brighter">
			<button
				onClick={onClick}
				type="button"
				className="h-full w-full text-pretty p-4 text-left text-sm font-semibold leading-tight text-dm-mid-grey"
			>
				<span className="mb-1.5 block w-fit rounded-lg bg-dm-black p-2.5">
					<Icon className="h-[0.875rem] w-[0.875rem]" />
				</span>
				<span className="block max-w-[15ch] lg:max-w-full">{text}</span>
			</button>
		</li>
	);
};

export const OnboardingWelcome = () => {
	const { nextPage } = useOnboardingContext();
	const { mutateAsync: updateOnboardingStatus } = useUpdateOnboardingStatus();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	useEffect(() => {
		if (user?.onboarding?.status === 'skipped') {
			updateOnboardingStatus({
				status: 'pending'
			});
		}
	}, [updateOnboardingStatus, user?.onboarding?.status]);

	const handleNextPage = (analyticsEvent: string) => {
		nextPage();

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'Onboarding Flow - Submitted Intent on Welcome Page',
			properties: {
				selectedOption: analyticsEvent
			}
		});

		if (user?.onboarding?.status !== 'completed') {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'Onboarding Flow - Landed on Onboarding First Page',
				properties: {
					page: 'Step 1: Outreach Page'
				}
			});
		}
	};

	const welcomeCards = [
		{ icon: BookIcon, text: 'Book my first meetings' },
		{ icon: RepliesIcon, text: 'Get more replies to cold outreach' },
		{ icon: AiPowered, text: 'Explore AI-powered email' },
		{ icon: CheckingIcon, text: 'Just checking it out for now' }
	];

	return (
		<div className="flex h-full flex-col items-center overflow-y-auto rounded-tl-lg rounded-tr-lg border-dm-gray px-6 pb-14 pt-10 text-center text-dm-white lg:rounded-tr-none lg:border-l-[0.0625rem] lg:border-t-[0.0625rem] lg:pt-14">
			<h1 className="flex flex-col items-center gap-x-3 gap-y-3.5 text-3xl font-semibold lg:flex-row">
				Hi! Welcome to
				<LogoText2
					aria-label="Resquared"
					className="h-[2.45rem] w-[9.3rem] translate-y-0.5"
				/>
			</h1>

			<div className='aspect-[256/257] h-[16rem] min-h-[10rem] bg-[url("../../../resources/assets/images/welcome-illustration.svg")] bg-contain bg-center bg-no-repeat' />

			<h2 className="text-lg font-semibold leading-loose">
				A fast, AI-powered platform for{' '}
				<br className="hidden lg:block" /> discovering and contacting
				local businesses
			</h2>

			<p className="mb-8 mt-2.5 text-[1.0625rem] font-semibold text-dm-mid-grey lg:mb-14">
				Build targeted lists, send personalized outreach, and track
				replies — all in one place
			</p>

			<div>
				<strong className="mb-4 block font-semibold text-dm-mid-grey">
					How can we help?
				</strong>

				<ul className="grid max-w-[44rem] grid-cols-1 gap-6 xxs:grid-cols-2 lg:grid-cols-4">
					{welcomeCards.map(card => (
						<WelcomeCard
							key={card.text}
							icon={card.icon}
							text={card.text}
							onClick={() => handleNextPage(card.text)}
						/>
					))}
				</ul>
			</div>
		</div>
	);
};
